import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useCanResume } from "@/hooks/useCanResume";
import { useIsSignedInState } from "@/hooks/useIsSignedIn";
import { getLastLesson } from "@/lib/progress.functions";

const mono = { fontFamily: '"JetBrains Mono", monospace' } as const;
const COURSE_LESSON_BASE =
  "/courses/business-and-accountancy/basic-business-accountancy/lessons";
const DEFAULT_LESSON_PATH =
  "/courses/business-and-accountancy/basic-business-accountancy/lessons/1.1";

function resolveResumeTarget(): string {
  try {
    return window.localStorage.getItem("wk-last-lesson") ?? DEFAULT_LESSON_PATH;
  } catch {
    return DEFAULT_LESSON_PATH;
  }
}

const navLinkClass =
  "interactive-lift interactive-lift-quiet inline-block font-bold uppercase tracking-[0.05em]";
const navLinkRestStyle = {
  ...mono,
  fontSize: "11px",
  color: "#1A1612",
  padding: "14px 20px",
  margin: "-14px -20px",
} as const;
const navLinkActiveStyle = {
  ...mono,
  fontSize: "11px",
  color: "#8B1E1E",
  padding: "14px 20px",
  margin: "-14px -20px",
} as const;
const separatorStyle = { ...mono, fontSize: "11px", color: "#5A534A" } as const;

// Mobile inline link
const mobileLinkBase = {
  fontFamily: '"Courier New", monospace',
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  textDecoration: "none",
  display: "inline-block",
  padding: "18px 8px",
  margin: "-18px -8px",
};
const mobileLinkRest = { ...mobileLinkBase, color: "#1A1612" };
const mobileLinkActive = { ...mobileLinkBase, color: "#8B1E1E" };

function interleaveSeparators(items: ReactNode[], keyPrefix: string): ReactNode[] {
  return items.flatMap((item, i) =>
    i === 0
      ? [item]
      : [
          <span key={`${keyPrefix}-sep-${i}`} className="font-bold" style={separatorStyle}>
            ·
          </span>,
          item,
        ],
  );
}

export function SiteHeader() {
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const hideNav =
    pathname === "/start" ||
    pathname.startsWith("/auth") ||
    pathname.startsWith("/redeem");
  const canResume = useCanResume();
  const signedInState = useIsSignedInState();
  const navigate = useNavigate();
  const [serverLastLesson, setServerLastLesson] = useState<string | null>(null);

  useEffect(() => {
    if (signedInState !== "yes") return;
    let cancelled = false;
    getLastLesson()
      .then((res) => {
        if (cancelled) return;
        if (res?.lessonId) {
          setServerLastLesson(`${COURSE_LESSON_BASE}/${res.lessonId}`);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [signedInState]);

  const isOnMyCourses = pathname === "/my-courses" || pathname.startsWith("/my-courses/");
  const isOnAccount = pathname === "/account" || pathname.startsWith("/account/");
  const isOnSignin = pathname === "/signin";

  function handleResumeClick(e: React.MouseEvent) {
    e.preventDefault();
    const target = serverLastLesson ?? resolveResumeTarget();
    navigate({ to: target });
  }

  return (
    <>
      {/* Mobile-only opaque strip behind the fixed header to prevent content
          from scrolling visibly under the transparent wordmark/nav. */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-[99] bg-paper"
        style={{ height: "60px" }}
        aria-hidden="true"
      />

      {/* Wordmark — fixed top-left. */}
      <header className="fixed top-0 left-0 z-[100] pt-3 pl-4 md:pt-6 md:pl-10 flex items-center">
        <Link
          to="/"
          className="interactive-lift interactive-lift-quiet inline-block md:bg-paper font-bold text-[20px] tracking-[0.1em] text-burgundy p-[14px_20px] max-[360px]:p-[18px_20px] max-[360px]:[margin:-18px_-20px] md:[margin:-14px_-20px]"
          style={mono}
        >
          WHO KNEW
        </Link>
      </header>


      {!hideNav && signedInState !== "loading" && (
        <>
          {/* Desktop nav */}
          <nav className="hidden md:flex fixed top-0 right-0 z-[100] items-center gap-2 pt-6 pr-10">
            <div
              className="flex items-center gap-2 bg-paper"
              style={{ padding: "14px 20px", margin: "-14px -20px" }}
            >
              {signedInState === "no" ? (
                !isOnSignin && (
                  <Link
                    to="/signin"
                    className={navLinkClass}
                    style={navLinkRestStyle}
                    activeProps={{ style: navLinkActiveStyle }}
                  >
                    Sign in
                  </Link>
                )
              ) : (
                (() => {
                  const items: ReactNode[] = [];
                  if (canResume) {
                    items.push(
                      <button
                        key="resume"
                        type="button"
                        onClick={handleResumeClick}
                        className={navLinkClass + " bg-transparent"}
                        style={navLinkRestStyle}
                      >
                        Resume course
                      </button>,
                    );
                  }
                  if (!isOnMyCourses) {
                    items.push(
                      <Link
                        key="my-courses"
                        to="/my-courses"
                        className={navLinkClass}
                        style={navLinkRestStyle}
                        activeProps={{ style: navLinkActiveStyle }}
                      >
                        My courses
                      </Link>,
                    );
                  }
                  if (!isOnAccount) {
                    items.push(
                      <Link
                        key="my-account"
                        to="/account"
                        className={navLinkClass}
                        style={navLinkRestStyle}
                        activeProps={{ style: navLinkActiveStyle }}
                      >
                        My account
                      </Link>,
                    );
                  }
                  return interleaveSeparators(items, "desk");
                })()
              )}
            </div>
          </nav>

          {/* Mobile nav */}
          <nav
            className="md:hidden fixed top-0 right-0 z-[100] pt-[31px] pr-4 flex items-center"
            style={{ gap: "4px" }}
            aria-label="Primary"
          >
            {signedInState === "no" ? (
              !isOnSignin && (
                <Link
                  to="/signin"
                  className="inline-block"
                  style={mobileLinkRest}
                  activeProps={{ style: mobileLinkActive }}
                >
                  Sign in
                </Link>
              )
            ) : (
              (() => {
                const items: ReactNode[] = [];
                if (canResume) {
                  items.push(
                    <button
                      key="resume"
                      type="button"
                      onClick={handleResumeClick}
                      className="bg-transparent inline-block"
                      style={mobileLinkRest}
                    >
                      Resume
                    </button>,
                  );
                }
                if (!isOnMyCourses) {
                  items.push(
                    <Link
                      key="my-courses"
                      to="/my-courses"
                      className="inline-block"
                      style={mobileLinkRest}
                      activeProps={{ style: mobileLinkActive }}
                    >
                      <span className="max-[440px]:hidden">My courses</span>
                      <span className="hidden max-[440px]:inline">Courses</span>
                    </Link>,
                  );
                }
                if (!isOnAccount) {
                  items.push(
                    <Link
                      key="my-account"
                      to="/account"
                      className="inline-block"
                      style={mobileLinkRest}
                      activeProps={{ style: mobileLinkActive }}
                    >
                      <span className="max-[440px]:hidden">My account</span>
                      <span className="hidden max-[440px]:inline">Account</span>
                    </Link>,
                  );
                }
                return interleaveSeparators(items, "mob");
              })()
            )}
          </nav>

        </>
      )}
    </>
  );
}
